import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { RecurringTask, CreateRecurringTaskDTO, UpdateRecurringTaskDTO } from '../../services/api/todo.service';

interface RecurringTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (recurring: CreateRecurringTaskDTO | UpdateRecurringTaskDTO) => void;
  taskId: string;
  taskTitle: string;
  existingRecurring?: RecurringTask | null;
}

const RecurringTaskDialog: React.FC<RecurringTaskDialogProps> = ({
  open,
  onClose,
  onSave,
  taskId,
  taskTitle,
  existingRecurring
}) => {
  const [pattern, setPattern] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('weekly');
  const [interval, setInterval] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [monthOfYear, setMonthOfYear] = useState<number>(1);
  const [endDate, setEndDate] = useState('');
  const [maxOccurrences, setMaxOccurrences] = useState<number | undefined>();
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (existingRecurring) {
      setPattern(existingRecurring.pattern);
      setInterval(existingRecurring.interval);
      setDaysOfWeek(existingRecurring.daysOfWeek || []);
      setDayOfMonth(existingRecurring.dayOfMonth || 1);
      setMonthOfYear(existingRecurring.monthOfYear || 1);
      setEndDate(existingRecurring.endDate || '');
      setMaxOccurrences(existingRecurring.maxOccurrences);
      setIsActive(existingRecurring.isActive);
    } else {
      // Reset to defaults for new recurring task
      setPattern('weekly');
      setInterval(1);
      setDaysOfWeek([]);
      setDayOfMonth(1);
      setMonthOfYear(1);
      setEndDate('');
      setMaxOccurrences(undefined);
      setIsActive(true);
    }
  }, [existingRecurring, open]);

  const handleSave = () => {
    const recurringData: CreateRecurringTaskDTO | UpdateRecurringTaskDTO = {
      pattern,
      interval,
      ...(pattern === 'weekly' && { daysOfWeek }),
      ...(pattern === 'monthly' && { dayOfMonth }),
      ...(pattern === 'yearly' && { dayOfMonth, monthOfYear }),
      ...(endDate && { endDate }),
      ...(maxOccurrences && { maxOccurrences }),
      ...(existingRecurring && { isActive })
    };

    onSave(recurringData);
  };

  const handleDayOfWeekToggle = (day: number) => {
    setDaysOfWeek(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const getDayName = (day: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  };

  const getPatternDescription = () => {
    switch (pattern) {
      case 'daily':
        return `Every ${interval} day${interval > 1 ? 's' : ''}`;
      case 'weekly':
        const dayNames = daysOfWeek.map(d => getDayName(d)).join(', ');
        return `Every ${interval} week${interval > 1 ? 's' : ''} on ${dayNames || 'no days selected'}`;
      case 'monthly':
        return `Every ${interval} month${interval > 1 ? 's' : ''} on day ${dayOfMonth}`;
      case 'yearly':
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
        return `Every ${interval} year${interval > 1 ? 's' : ''} on ${months[monthOfYear - 1]} ${dayOfMonth}`;
      default:
        return '';
    }
  };

  const isValid = () => {
    if (pattern === 'weekly' && daysOfWeek.length === 0) return false;
    if (interval < 1) return false;
    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <RepeatIcon color="primary" />
          <Typography variant="h6">
            {existingRecurring ? 'Edit Recurring Task' : 'Make Task Recurring'}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} style={{ marginTop: 8 }}>
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Task: {taskTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure how often this task should recur
            </Typography>
          </div>

          {/* Pattern Selection */}
          <FormControl fullWidth>
            <InputLabel>Recurrence Pattern</InputLabel>
            <Select
              value={pattern}
              onChange={(e) => setPattern(e.target.value as any)}
              label="Recurrence Pattern"
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>

          {/* Interval */}
          <TextField
            label={`Every ${pattern === 'daily' ? 'X days' : pattern === 'weekly' ? 'X weeks' :
                   pattern === 'monthly' ? 'X months' : 'X years'}`}
            type="number"
            value={interval}
            onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
            fullWidth
            inputProps={{ min: 1, max: 365 }}
          />

          {/* Weekly Days Selection */}
          {pattern === 'weekly' && (
            <div>
              <Typography variant="subtitle2" gutterBottom>
                Days of the Week
              </Typography>
              <FormGroup row>
                {[0, 1, 2, 3, 4, 5, 6].map(day => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={daysOfWeek.includes(day)}
                        onChange={() => handleDayOfWeekToggle(day)}
                      />
                    }
                    label={getDayName(day).substring(0, 3)}
                  />
                ))}
              </FormGroup>
            </div>
          )}

          {/* Monthly Day Selection */}
          {pattern === 'monthly' && (
            <TextField
              label="Day of Month"
              type="number"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
              fullWidth
              inputProps={{ min: 1, max: 31 }}
              helperText="Day 29-31 may not exist in all months"
            />
          )}

          {/* Yearly Date Selection */}
          {pattern === 'yearly' && (
            <Stack direction="row" spacing={2}>
              <FormControl style={{ flex: 1 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={monthOfYear}
                  onChange={(e) => setMonthOfYear(e.target.value as number)}
                  label="Month"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Day"
                type="number"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                style={{ flex: 1 }}
                inputProps={{ min: 1, max: 31 }}
              />
            </Stack>
          )}

          {/* End Conditions */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              End Conditions (Optional)
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for no end date"
              />
              <TextField
                label="Maximum Occurrences"
                type="number"
                value={maxOccurrences || ''}
                onChange={(e) => setMaxOccurrences(e.target.value ? parseInt(e.target.value) : undefined)}
                inputProps={{ min: 1 }}
                helperText="Leave empty for unlimited occurrences"
              />
            </Stack>
          </div>

          {/* Active Toggle for Editing */}
          {existingRecurring && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active (generates new instances)"
            />
          )}

          {/* Pattern Preview */}
          <div style={{ padding: 16, backgroundColor: '#fafafa', borderRadius: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ScheduleIcon fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight="medium">
                Pattern: {getPatternDescription()}
              </Typography>
            </Stack>
            {endDate && (
              <Typography variant="caption" color="text.secondary">
                Until: {new Date(endDate).toLocaleDateString()}
              </Typography>
            )}
            {maxOccurrences && (
              <Typography variant="caption" color="text.secondary">
                Maximum: {maxOccurrences} occurrences
              </Typography>
            )}
          </div>

          {/* Validation Warning */}
          {!isValid() && (
            <Alert severity="warning">
              {pattern === 'weekly' && daysOfWeek.length === 0 &&
                'Please select at least one day of the week'}
              {interval < 1 && 'Interval must be at least 1'}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid()}
          startIcon={<RepeatIcon />}
        >
          {existingRecurring ? 'Update Recurring' : 'Make Recurring'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringTaskDialog;